import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  ErrorModalComponent,
  ModalRef,
  ModalService,
  ReactiveComponent,
  TwoButtonModalV2Component,
  ModalModule,
  SelectV2Module,
  IconV2Module,
  InputV2Module,
  ButtonV2Module,
} from '@schenck/rotec-components';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { UserGridKeys, UserRole } from '../../../enums/user.enum';
import { UserService } from '../../../services/user.service';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { USER_ROLES } from 'src/const/user.const';
import { UserModel } from 'src/models/user.model';
import { TranslateService, TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';

@Component({
  selector: 'create-edit-user-modal',
  templateUrl: './create-edit-user-modal.component.html',
  styleUrls: ['./create-edit-user-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalModule,
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    TranslatePipe,
    TranslateDirective,
    SelectV2Module,
    IconV2Module,
    InputV2Module,
    ButtonV2Module,
    AsyncPipe,
  ],
})
export class CreateEditUserModalComponent extends ReactiveComponent {
  private readonly modalRef = inject<ModalRef<UserModel>>(ModalRef);
  private readonly spinnerService = inject(SpinnerService);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);
  public userService = inject(UserService);

  USER_ROLES = USER_ROLES.filter((res) => res != UserRole.RoTec);

  public width = '720px';
  public height: '700px';
  public edit = false;
  public errorMessage: string;
  public loading$ = this.spinnerService.spinnerActive$;
  public form: UntypedFormGroup;
  public noOfAdminRoles: number = 0;

  constructor() {
    super();
    this.edit = !!this.modalRef.data;
    this.setForm();
    this.edit && this.form.patchValue(this.modalRef.data);
    this.userService.users$.pipe(filter((users) => !!users)).subscribe((users) => {
      this.noOfAdminRoles = users.filter((res) => res.role === UserRole.Admin).length;
    });
  }

  public save(): void {
    if (
      this.edit &&
      this.modalRef.data.role == UserRole.Admin &&
      this.form.getRawValue().role != UserRole.Admin
    ) {
      const warning = this.noOfAdminRoles > 1;
      const text = warning
        ? 'Are you sure that you want to change the Admin role to the ROLE_NAME role?'
        : 'This is the only Admin Account and therefore changing the role to ROLE_NAME is not possible.';
      const modalData = {
        title: warning ? 'Warning' : 'COMMON_Error',
        text: this.translateService.instant(text, { roleName: this.form.getRawValue().role }),
        ...(warning && {
          confirmText: 'COMMON_Confirm',
          cancelText: 'COMMON_Cancel',
          warning: true,
          darkMode: true,
        }),
      };
      this.modalService
        .open(warning ? TwoButtonModalV2Component : ErrorModalComponent, modalData)
        .events$.pipe(
          takeUntil(this.onDestroy$),
          filter((value) => !!value)
        )
        .subscribe(() => this.saveUserDetail());
    } else {
      this.saveUserDetail();
    }
  }

  public hasControlError(control: AbstractControl): boolean {
    return control?.invalid && control?.touched;
  }

  public close(): void {
    this.modalRef.close();
  }

  public closeErrorModal(): void {
    this.errorMessage = '';
  }

  private saveUserDetail(): void {
    const formData = this.form.getRawValue();
    const user = {
      ...(this.edit && this.modalRef.data),
      ...formData,
    };

    this.userService
      .saveUser$(user, this.edit)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          throw Error(error?.message);
        })
      )
      .subscribe(() => {
        this.modalRef.emitAndClose(true);
      });
  }

  private setForm(): void {
    this.form = new UntypedFormGroup({
      [UserGridKeys.GivenName]: new UntypedFormControl('', [Validators.maxLength(50)]),
      [UserGridKeys.Surname]: new UntypedFormControl('', [Validators.maxLength(50)]),
      [UserGridKeys.Role]: new UntypedFormControl(UserRole.Operator),
      ...(!this.edit && {
        [UserGridKeys.Email]: new UntypedFormControl('', [Validators.email, this.emailValidator()]),
      }),
    });
  }

  private emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let existingUserEmailCount: number = 0;
      const emailRegEx: RegExp =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

      if (control.value && !emailRegEx.test(control.value)) {
        return { errorMessage: 'The email is invalid.' };
      }
      this.userService.users$
        .pipe(
          takeUntil(this.onDestroy$),
          filter((users) => !!users)
        )
        .subscribe((users) => {
          existingUserEmailCount = users.filter(
            (res) => res.email.toLocaleLowerCase() === control?.value?.toLocaleLowerCase()
          ).length;
        });
      if (control.value && existingUserEmailCount > 0) {
        return { errorMessage: 'E-Mail Address already exists.' };
      }
      return null;
    };
  }
}
